import { useFetcher } from "@remix-run/react";
import { ShirtspaceResponseMessage } from "@services/shirtspace.types";
import get from "lodash/get";
import { useEffect, useRef, useState } from "react";
import FlashMessage from "~/components/FlashMessage";
import ReCaptchaStatement from "~/components/ReCaptchaStatement";
import { CreateAccountActionResponse } from "~/entities/actions/types";
import FieldError from "./FieldError";

const ErrorMessage: React.FC<{
  error?: ShirtspaceResponseMessage;
}> = ({ error }) => {
  if (!error) return null;
  return (
    <FlashMessage
      kind="failure"
      message={error.text}
    />
  );
};

type Props = {
  onSuccess: (customerType: string) => void;
  isActive: boolean;
};

const CreateAccount: React.FC<Props> = ({ onSuccess, isActive }) => {
  const [errors, setErrors] = useState<
    Record<string, ShirtspaceResponseMessage>
  >({});
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const fetcher = useFetcher<CreateAccountActionResponse>();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmationRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPassword("");
    setPasswordConfirmation("");
    if (
      get(fetcher, "data.ok") === true &&
      get(fetcher, "data.data.customer_type")
    ) {
      setErrors({});
      const customerType = get(fetcher, "data.data.customer_type");
      if (typeof customerType === "string") onSuccess(customerType);
    } else {
      const messages = get(fetcher, "data.messages", []);

      const errorsHash = messages.reduce(
        (
          hash: Record<string, ShirtspaceResponseMessage>,
          message: ShirtspaceResponseMessage,
        ) => {
          hash[message.attribute ?? "form"] = message;
          return hash;
        },
        {},
      );
      setErrors(errorsHash);
    }
  }, [fetcher.data]);

  useEffect(() => {
    if (errors["email"] && emailRef.current) {
      emailRef.current.focus();
    } else if (
      (errors["password"] || errors["password_confirmation"]) &&
      passwordRef.current
    ) {
      passwordRef.current.focus();
    } else if (isActive && emailRef.current) {
      emailRef.current.focus();
    }
  }, [isActive, errors]);

  if (!isActive) return null;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === "password") {
      setPassword(event.target.value);
    } else if (event.target.name === "password_confirmation") {
      setPasswordConfirmation(event.target.value);
    }
  };

  const handlePasswordIconClick = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  return (
    <fetcher.Form
      method="post"
      action="/resources/create-account-wizard"
      className="flex flex-col gap-7 xsm:gap-4 md:gap-3.5 px-5"
      data-testid="create-account-form"
    >
      <input
        type="hidden"
        name="intent"
        value="create_account"
      />

      <ErrorMessage error={errors["form"]} />

      <div className="flex flex-col gap-0.5">
        <label
          className="text-sm leading-none text-blue900"
          htmlFor="email"
        >
          Email
        </label>
        <input
          aria-label="Enter email to create an account"
          ref={emailRef}
          id="email"
          className={`h-8 rounded border ${errors["email"] ? "border-red200" : "border-black"} p-2`}
          type="text"
          name="email"
        />
        <FieldError fieldError={errors["email"]} />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-sm leading-[0.5] text-blue900"
          htmlFor="password"
        >
          Password
        </label>
        <div className="relative flex">
          <input
            aria-label="Enter password"
            ref={passwordRef}
            id="password"
            className={`h-8 w-full rounded border ${errors["password"] ? "border-red200" : "border-black"} p-2`}
            type={isPasswordVisible ? "text" : "password"}
            name="password"
            value={password}
            onChange={handleInputChange}
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            className="absolute right-6 top-1.5 block"
            onClick={handlePasswordIconClick}
          >
            <svg
              className="fill-gray"
              width="20"
              height="20"
            >
              <use
                xlinkHref={`/frontend-assets/images/icons.svg#eye-${isPasswordVisible ? "open" : "closed"}`}
              />
            </svg>
          </button>
        </div>
        <FieldError fieldError={errors["password"]} />
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-sm leading-[0.5] text-blue900"
          htmlFor="password_confirmation"
        >
          Password Confirmation
        </label>
        <div className="relative flex">
          <input
            aria-label="Confirm password"
            ref={passwordConfirmationRef}
            id="password_confirmation"
            className={`h-8 w-full rounded border ${errors["password_confirmation"] ? "border-red200" : "border-black"} p-2`}
            type={isPasswordVisible ? "text" : "password"}
            name="password_confirmation"
            value={passwordConfirmation}
            onChange={handleInputChange}
          />
          <button
            type="button"
            aria-label="Toggle password visibility"
            className="absolute right-6 top-1.5 block"
            onClick={handlePasswordIconClick}
          >
            <svg
              className="fill-gray"
              width="20"
              height="20"
            >
              <use
                xlinkHref={`/frontend-assets/images/icons.svg#eye-${isPasswordVisible ? "open" : "closed"}`}
              />
            </svg>
          </button>
        </div>
        <FieldError fieldError={errors["password_confirmation"]} />
      </div>

      <fieldset className="flex flex-col gap-1">
        <legend className="block text-sm text-blue900 mb-1">
          What Best Describes Your Use For Apparel?
        </legend>
        <div className="grid grid-cols-2 gap-1">
          <div className="flex items-center gap-1">
            <input
              id="business"
              type="radio"
              name="customer_type"
              value="business"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="business"
            >
              Business
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="school"
              type="radio"
              name="customer_type"
              value="school"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="school"
            >
              School
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="member"
              type="radio"
              name="customer_type"
              value="member"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="member"
            >
              Personal
            </label>
          </div>
          <div className="flex items-center gap-1">
            <input
              id="charity"
              type="radio"
              name="customer_type"
              value="charity"
            />
            <label
              className="text-sm text-blue900"
              htmlFor="charity"
            >
              Charity
            </label>
          </div>
        </div>
        <FieldError fieldError={errors["customer_type"]} />
      </fieldset>

      <div className="flex items-center justify-start gap-2">
        <button
          type="submit"
          className="h-8 w-auto self-start rounded-md bg-blue hover:bg-blue200 disabled:bg-blue500 px-4 text-lg uppercase text-white"
          disabled={fetcher.state === "submitting"}
        >
          {fetcher.state === "submitting" ? "Submitting..." : "Create account"}
        </button>
        <p className="text-gray text-sm">
          Or{" "}
          <a
            className="text-sm text-blue900 underline"
            href="/users/sign-up"
          >
            Sign in to your account
          </a>
        </p>
      </div>
      <ReCaptchaStatement />
    </fetcher.Form>
  );
};

export default CreateAccount;
